<template>
  <div id="app">
    <RouterView />
    <div
      v-if="
        !isRouteTrackingPage &&
        JSON.parse(getFeatureFlagData('freshchat_support_customized').value).show &&
        JSON.parse(getFeatureFlagData('freshchat_support_customized').value).custom_button &&
        hasAccess(['0e0-base-livechat:read'])
      "
      id="mc-freshchat"
      style="cursor: pointer; position: fixed"
      :style="`left: ${position.x}px; top: ${position.y}px;`"
      @click="handleWidgetClick"
      @mousedown="startDraggingFreshChat"
    >
      <span id="open_fc_widget">
        <i id="mc-freshchat__imageicon" class="mc-fa icon-fill_message-circle align-bottom">
          <McBadge v-if="showMessageNotif" class="p-1" variant="danger" size="small" type="notif" />
        </i>
      </span>
      <div id="mc-freshchat__text">
        {{ $t('livechat.label') }}
      </div>
    </div>
    <span v-show="scY > navbarHeight" id="pagetop" class="mc-totop mc-primary" @click="toTop">
      <i class="mc-fa icon-outline_arrow-ios-up fw-bold"></i>
      <!--  eslint-disable-next-line i18next/no-literal-string -->
      <span class="font-size-12 fw-bold ps-1"> Back to Top </span>
    </span>
    <WidgetCall2Way v-if="hasAccess('b3f-two-way-communication:read')" />
    <WidgetDownload />
    <portal-target name="alert-center" />
    <portal-target
      v-for="popup in $store.state.notification.activePopups"
      :key="popup.id"
      :name="`modal-draggable-${popup.id}`"
    />
    <portal-target
      v-for="popup in $store.state.controlTower.activePopups"
      :key="popup.id"
      :name="`modal-draggable-${popup.id}`"
    />
    <portal-target name="modal-onboarding" />
  </div>
</template>

<script>
import { identityComputed, notificationMethods, freshchatComputed } from '@/state/helpers'

import { initializeApp } from 'firebase/app'
import { deleteToken, getMessaging, getToken, onMessage } from 'firebase/messaging'
import { Helper } from './mixin/helper'
import { setColor } from './helpers/configColors'
import { getColor } from '@/services/rest-api/lib/personalization/color'

import { REGISTER_FCM_TOKEN } from '@/services/api/fcm'
import { datadogRum } from '@datadog/browser-rum'
import McBadge from '@/components/McBadge'
import { MixPanel } from '@/mixin/mixpanel'
import { userGuiding } from '@/mixin/userGuiding'
import { unregisterFCMServiceWorkers } from './helpers/unregister-service-workers'
import { formatDistance } from './helpers/format-distance'

import WidgetCall2Way from './router/layouts/components/Vertical/two-way-communication/index.vue'
import WidgetDownload from './components/WidgetDownload.vue'
import { mapGetters } from 'vuex'

const soundNotification = require('@/assets/sounds/notification_sound.wav')

export default {
  name: 'MainApp',
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title
      let titleName = ''
      if (title) {
        titleName = this.$store.state.asset.data.tabTitle.general
          ? `${title} | ${this.$store.state.asset.data.tabTitle.general}`
          : title
      } else {
        titleName = this.$store.state.asset.data.tabTitle.general
      }
      return titleName
    },
  },
  components: { McBadge, WidgetCall2Way, WidgetDownload },
  mixins: [Helper, MixPanel, userGuiding],
  data() {
    return {
      scTimer: 0,
      scY: 0,
      navbarHeight: 70,
      restoreId: null,

      typeMappings: {
        WAITING_FOR_VENDOR: {
          toastType: 'info',
          redirectRoute: 'Delivery Request Detail',
          keyParam: 'vendor_request_id',
        },
        ACCEPTED: {
          toastType: 'info',
          redirectRoute: 'Delivery Detail',
          keyParam: 'delivery_order_id',
        },
        REJECTED: {
          toastType: 'warning',
          redirectRoute: 'Delivery Detail',
          keyParam: 'delivery_order_id',
        },
      },

      position: {
        x: window.innerWidth - 200,
        y: window.innerHeight - 50,
      },
      isDragging: false,
      mouseDownTime: 0,
      mouseMoved: false,
    }
  },
  computed: {
    ...identityComputed,
    ...freshchatComputed,
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    isRouteTrackingPage() {
      // Check both route name and path to handle slow connections
      return (
        this.$route.name === 'Route Tracking' ||
        this.$route.path === '/route/monitoring/public-tracking' ||
        this.$route.path.includes('/route/monitoring/public-tracking')
      )
    },
  },
  watch: {
    /**
     * Clear the alert message on route change
     */
    // eslint-disable-next-line no-unused-vars
    $route(to, from) {
      // clear alert on location change
      this.clearNotification()
      // reset address when location is changed
      this.$store.commit('map/RESET_ADDRESS_INFO')
    },

    '$store.state.identity.whoami'(whoami) {
      if (whoami) {
        datadogRum.setUser({
          id: whoami.userId,
          name: whoami.name,
          email: whoami.email,
          role: whoami.accessRight,
          companyId: whoami.companyId,
          companyName: whoami.companyName,
        })
      }
    },
  },
  // created() {
  //   const isWhitelabel = JSON.parse(process.env.VUE_APP_ISWHITELABEL)
  //   if (!isWhitelabel) {
  //     const link = document.createElement('link')
  //     link.rel = 'manifest'
  //     link.href = '/manifest.json'
  //     document.head.appendChild(link)
  //   }
  // },
  async mounted() {
    this.identifierUserGuiding()

    this.$store.dispatch('asset/fillAssetsURL').then(() => {
      if (this.$store.state.asset.data.tabTitle.general) {
        document.title += ` | ${this.$store.state.asset.data.tabTitle.general}`
      }
      const favicon = document.querySelectorAll("link[rel='icon']")
      favicon.forEach((element) => {
        element.href = this.$store.state.asset.data.favicon.general
      })

      const thumbnail = document.querySelectorAll("meta[property='og:image']")
      thumbnail.forEach((element) => {
        element.content = this.$store.state.asset.data.secondaryLogo.sidebarMinimize
      })
    })

    // mocking backend response
    const getCustomColor = await getColor()
    const customColor = getCustomColor.data
    await setColor(customColor)

    window.addEventListener('scroll', this.handleScroll)

    if (!localStorage.getItem('notification-popup')) {
      localStorage.setItem(
        'notification-popup',
        JSON.stringify(this.$store.state.notification.config)
      )
    } else {
      this.$store.commit(
        'notification/configPopUp',
        JSON.parse(localStorage.getItem('notification-popup'))
      )
    }

    //adding delay for get localstorage payload exist
    await new Promise((resolve) => setTimeout(resolve, 5000))

    if (localStorage.getItem('payload')) {
      const self = this

      // Initialize the Firebase app in the service worker by passing in
      // your app's Firebase config object.
      // https://firebase.google.com/docs/web/setup#config-object
      const firebaseApp = initializeApp(
        {
          apiKey: process.env.VUE_APP_NOTIF_FIREBASE_API_KEY,
          authDomain: process.env.VUE_APP_NOTIF_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.VUE_APP_NOTIF_FIREBASE_PROJECT_ID,
          storageBucket: process.env.VUE_APP_NOTIF_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.VUE_APP_NOTIF_FIREBASE_MESSAGE_ID,
          appId: process.env.VUE_APP_NOTIF_FIREBASE_APP_ID,
          measurementId: process.env.VUE_APP_NOTIF_FIREBASE_MEASUREMENT_ID,
        },
        'vsmsNotif'
      )

      // Retrieve an instance of Firebase Messaging so that it can handle background
      // messages.
      const messaging = getMessaging(firebaseApp)

      // We are resubscribing to topic every time the app loads
      // So, resetting the token should be just fine (to prevent cross-company notifications)
      // await deleteToken(messaging)

      await Notification.requestPermission().then(async function (permission) {
        if (permission === 'granted') {
          const lastTokenTime = localStorage.getItem('lastTokenTime')
          const ONE_DAY = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
          let token = localStorage.getItem('fcmToken')
          if (!lastTokenTime || Date.now() - parseInt(lastTokenTime) > ONE_DAY) {
            await getToken(messaging, {
              vapidKey:
                'BJWUpIU_lc4VbNHBSS7Hg4KDGcZxWiaHdFQIy0t3XOYRTRsqpXTb6DR6yooVhvNPScRFPVzoX28CxQV89cvFtRM',
            })
              .then((currentToken) => {
                if (currentToken) {
                  token = currentToken
                  localStorage.setItem('fcmToken', currentToken)
                  localStorage.setItem('lastTokenTime', Date.now().toString())
                } else {
                  console.log(
                    'No registration token available. Request permission to generate one.'
                  )
                }
              })
              .catch((err) => {
                console.log('An error occurred while retrieving token. ', err)
              })
          }

          await self.$apollo.mutate({
            mutation: REGISTER_FCM_TOKEN,
            variables: {
              input: {
                registrationToken: token,
              },
            },
          })

          onMessage(messaging, (payload) => {
            const notifName = payload.data.notification_name
            if (notifName === 'delms.vendor') {
              self.createNotifToast(payload)

              return
            } else if (
              [
                'fleetTaskInstant.lateVisitingNotification',
                'fleetTaskInstant.lateServiceTimeNotification',
                'fleetTaskInstant.latePotentialNotification',
              ].includes(notifName)
            ) {
              self.createLateNotifToast(payload.data)

              return
            }

            const data = JSON.parse(payload.data.data)

            if (self.$store.state.identity.whoami?.companyId !== data.companyId) {
              return
            }

            if (!self.hasAccess(['e4a-alert-center-main:read'])) {
              const soundAlert = self.$store.state.notification.config.alertSounds
              if (soundAlert.includes(data.alertCode)) {
                let audio = new Audio(soundNotification)
                audio.play()
              }
              self.$toast.warning(`${payload.notification.title}, ${payload.notification.body}`, {
                position: 'bottom-right',
                timeout: self.$store.state.notification.config.time.key * 1000,
                closeOnClick: true,
                pauseOnFocusLoss: true,
                pauseOnHover: true,
                draggable: false,
                draggablePercent: 0.6,
                showCloseButtonOnHover: false,
                hideProgressBar: true,
                closeButton: 'button',
                icon: true,
                rtl: false,
              })
            }
          })
        }
      })
    } else unregisterFCMServiceWorkers(navigator)

    // Load script for VSS Howen trackvision
    const src = [
      'common.js',
      'webgl.js',
      'vss-player.js',
      'HWRecorder.js',
      'pcm-player.js',
      'HWMediaPlayer.js',
    ]

    const promises = []
    for (let i = 0; i < src.length; i++) {
      promises.push(this.loadScript(src[i]))
    }
    await Promise.all(promises)
  },
  methods: {
    clearNotification: notificationMethods.clear,

    handleScroll: function () {
      if (this.scTimer || window.location.pathname === '/liveview/') return
      this.scTimer = setTimeout(() => {
        this.scY = window.scrollY
        clearTimeout(this.scTimer)
        this.scTimer = 0
      }, 100)
    },
    toTop: function () {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    },

    handleWidgetClick() {
      setTimeout(() => {
        if (!this.isDragging) {
          this.openWidget()
        }
      }, 0)
    },

    startDraggingFreshChat(e) {
      this.mouseDownTime = Date.now()
      this.mouseMoved = false
      this.isDragging = true
      this.initialX = e.clientX - this.position.x
      this.initialY = e.clientY - this.position.y

      document.addEventListener('mousemove', this.onDrag)
      document.addEventListener('mouseup', this.handleMouseUp)
    },

    onDrag(e) {
      if (this.isDragging) {
        this.mouseMoved = true
        this.position.x = e.clientX - this.initialX
        this.position.y = e.clientY - this.initialY
      }
    },

    stopDragging() {
      if (this.isDragging) {
        const freshchat = document.getElementById('fc_frame')
        if (freshchat) freshchat.style.visibility = 'hidden'
        window.fcWidget.close()
      }

      setTimeout(() => {
        this.isDragging = false
      }, 0)

      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.stopDragging)
    },

    handleMouseUp(e) {
      const clickDuration = Date.now() - this.mouseDownTime

      if (!this.mouseMoved && clickDuration < 200) {
        this.openWidget()
      } else {
        const freshchat = document.getElementById('fc_frame')
        if (freshchat) freshchat.style.visibility = 'hidden'
        window.fcWidget.close()
      }

      this.isDragging = false
      this.mouseMoved = false
      document.removeEventListener('mousemove', this.onDrag)
      document.removeEventListener('mouseup', this.handleMouseUp)
    },
    openWidget() {
      const freshchat = document.getElementById('fc_frame')
      if (freshchat) freshchat.style.visibility = 'visible'
      window.fcWidget.open()
      this.initEvents()
      this.track('Click Livechat', {
        user_name: this.$store.state.identity.whoami?.name,
        email: this.$store.state.identity.whoami?.email,
        company_name: this.$store.state.identity.whoami?.companyName,
        access_right: this.$store.state.identity.whoami?.accessRight,
      })
    },
    initEvents() {
      const self = this
      // eslint-disable-next-line no-unused-vars
      window.fcWidget.on('widget:opened', function (resp) {
        self.$store.commit('freshchat/setFcOpened', true)
        if (document.getElementById('custom_fc_button'))
          document.getElementById('custom_fc_button').style.visibility = 'hidden'
      })

      // eslint-disable-next-line no-unused-vars
      window.fcWidget.on('widget:closed', function (resp) {
        self.$store.commit('freshchat/setFcOpened', false)
        if (document.getElementById('custom_fc_button'))
          document.getElementById('custom_fc_button').style.visibility = 'visible'
      })
    },

    handleClickToast(payload) {
      const id = payload.data[this.typeMappings[payload.data.status].keyParam]

      this.$router.push({
        name: this.typeMappings[payload.data.status].redirectRoute,
        params: { id },
      })
    },

    createNotifToast(payload) {
      const customToast = {
        component: 'McToastDynamicBody',
        listeners: {
          click: () => this.handleClickToast(payload),
        },
        props: {
          contents: [
            {
              tag: 'div',
              text: payload.notification.title,
              class: '__title-text cursor-pointer',
            },
            {
              tag: 'div',
              text: payload.notification.body,
              class: '__body-text',
            },
          ],
        },
      }

      this.$mcToast(customToast, {
        type: this.typeMappings[payload.data.status].toastType,
        position: 'bottom-right',
        timeout: 300000,
      })
    },
    loadScript(filename) {
      return new Promise((resolve, reject) => {
        // For scripts in public folder, use the relative path
        const src = `/vss/${filename}` // or process.env.BASE_URL + filename

        const script = document.createElement('script')
        script.src = src
        script.async = true

        script.onload = resolve
        script.onerror = reject

        document.head.appendChild(script)
      })
    },

    createLateNotifToast(data) {
      let keyLateNotif
      let isWarningToast = false

      if (data.notification_name === 'fleetTaskInstant.lateVisitingNotification') {
        keyLateNotif = 'lateVisiting'
      } else if (data.notification_name === 'fleetTaskInstant.lateServiceTimeNotification') {
        keyLateNotif = 'lateServiceTime'
      } else if (data.notification_name === 'fleetTaskInstant.latePotentialNotification') {
        keyLateNotif = 'latePotential'
        isWarningToast = true
      }

      const customToast = {
        component: 'McToastDynamicBody',
        listeners: {
          click: () => {
            this.$router.push({
              name: 'Fleet Task Instant Detail',
              params: { id: data.id },
            })
          },
        },
        props: {
          contents: [
            {
              html: () =>
                this.$t(`fleetTaskInstant.toast.lateNotification.title.${keyLateNotif}`, {
                  licensePlate: data.license_plate,
                  foNumber: data.fleet_task_number,
                }),
              tag: 'div',
            },
            {
              html: () =>
                this.$t('fleetTaskInstant.toast.lateNotification.content.driver', {
                  driverName: data.driver_user_name ?? '-',
                }),
              tag: 'content',
            },
            [
              'fleetTaskInstant.latePotentialNotification',
              'fleetTaskInstant.lateVisitingNotification',
            ].includes(data.notification_name)
              ? {
                  html: () =>
                    this.$t('fleetTaskInstant.toast.lateNotification.content.distance', {
                      distance: formatDistance(data.distance),
                    }),
                  tag: 'content',
                }
              : [],
            {
              html: () =>
                this.$t('fleetTaskInstant.toast.lateNotification.content.address', {
                  address: data.location,
                }),
              tag: 'content',
            },
          ],
        },
      }

      let lateToastOptions = {
        toastClassName: `mc-toast-alert mc-toast-alert--${
          isWarningToast ? 'warning' : 'error'
        } late-notification-toast`,
        bodyClassName: '__body-toast',
        type: isWarningToast ? 'warning' : 'error',
        position: 'bottom-right',
        timeout: 300000,
      }

      if (!isWarningToast) {
        lateToastOptions['icon'] = {
          iconClass: 'mc-toast-alert-icon icon-fill_alert-circle',
        }
      }

      this.$mcToast(customToast, lateToastOptions)
    },
  },
}
</script>
